import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { QUALITY } from '../constants/testIds';

const CARDS = [
  {
    grade: 'Grade A Premium',
    tread: '7mm+',
    pct: '80%+ tread remaining',
    desc: 'Top-tier matching pairs & sets. High tread life, clean sidewalls, zero structural defects. Ideal for high-margin retail resale.',
    badge: 'Highest Margin',
    testId: QUALITY.cardGradeA,
    image: '/images/brand/warehouse-aisle-blue.jpg',
    big: true,
  },
  {
    grade: 'Grade B Standard',
    tread: '5mm – 6.5mm',
    pct: '60%–75% tread',
    desc: 'Solid commercial standard. Even wear, pressure tested, highly requested for everyday replacement demand.',
    badge: 'Best Seller',
    testId: QUALITY.cardGradeB,
  },
  {
    grade: 'Grade C Commercial / Export',
    tread: '3.5mm – 4.5mm',
    pct: '40%–50% tread',
    desc: 'Budget tier designed for high-volume export and economical replacement markets.',
    badge: 'High Volume Export',
    testId: QUALITY.cardGradeC,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export const QualityTiers = () => (
  <section id="quality-tiers" data-testid={QUALITY.section} className="relative bg-brand-bg py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3">
        Grading Standards
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="font-display font-extrabold uppercase tracking-tight text-brand-text text-3xl sm:text-4xl lg:text-5xl max-w-3xl"
      >
        Tire Quality &amp; Tread Depth Grading
      </motion.h2>
      <p className="mt-4 max-w-2xl text-brand-muted text-base sm:text-lg">
        Strict multi-point inspection and tread-depth measurement on every single tire before it ships worldwide.
      </p>
      <Link to="/quality-grades" className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-brand-orange hover:text-brand-orangeHover transition-colors">
        Full grading criteria &rarr;
      </Link>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-5">
        {CARDS.map((c, i) => (
          <motion.div
            key={c.grade}
            custom={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            data-testid={c.testId}
            className={`relative rounded-2xl border border-brand-border overflow-hidden group ${
              c.big ? 'lg:col-span-1 lg:row-span-2 min-h-[22rem]' : 'lg:col-span-2 min-h-[13rem]'
            }`}
          >
            {c.image && (
              <>
                <img src={c.image} alt={c.grade} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-65 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/70 to-brand-bg/30" />
              </>
            )}
            {!c.image && <div className="absolute inset-0 bg-brand-surface" />}
            <div className="relative z-10 h-full flex flex-col justify-end p-7 sm:p-8">
              <span className="font-mono text-[10px] uppercase tracking-widest text-brand-amber bg-brand-bg/70 border border-brand-border rounded-full px-3 py-1 w-fit mb-4">
                {c.badge}
              </span>
              <h3 className="font-display font-bold text-2xl sm:text-3xl text-brand-text uppercase">{c.grade}</h3>
              <p className="font-mono text-brand-orange text-xl sm:text-2xl mt-2">{c.tread} <span className="text-brand-muted text-sm">· {c.pct}</span></p>
              <p className="mt-3 text-brand-muted text-sm sm:text-base max-w-md">{c.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
